import { useState } from 'react';
import { useFirestoreQuery } from '../../firebase/useFirestore';
import { db } from '../../firebase/config.js';
import useAutosaveField from '../../hooks/useAutosaveField';
import AutosaveStatus from '../common/AutosaveStatus';
import ReadOnlyGuard from '../common/ReadOnlyGuard';
import parseTariff from '../../helpers/personas/parseTariff';
import resolveBillingRules from '../../helpers/billing/resolveBillingRules';

/**
 * Instellingen → Ondersteuning → 'Facturatie': hoe deze organisatie dagdelen en
 * afwezigheid afrekent. Opgeslagen in `CompagnyMeta.BillingRules` en gelezen via
 * `resolveBillingRules`. Zonder complete regels rekent de facturatie niets — er
 * is bewust geen standaardregel (besluit Gijs, 2026-10-28).
 * AI-CONTEXT: See docs/features/facturatie-per-deelnemer.md
 * AI-CONTEXT: See docs/pitfalls.md#ui-select-defaultvalue-loses-late-options
 * AI-CONTEXT: See docs/pitfalls.md#state-per-keystroke-write-loses-the-tail
 */

const FIELD = 'BillingRules';

const METHOD_OPTIONS = [
    { value: 'perStartedBlock', label: 'Elk begonnen blok uren is een dagdeel' },
    { value: 'onePerDay', label: 'Elke aanwezige dag is één dagdeel' },
    { value: 'proRata', label: 'Naar verhouding (uren gedeeld door het blok)' },
];

const ABSENCE_OPTIONS = [
    { value: 'never', label: 'Nooit' },
    { value: 'always', label: 'Altijd' },
    { value: 'perReason', label: 'Alleen bij bepaalde redenen' },
];

const REASON_OPTIONS = [
    { value: 'ziek', label: 'Ziek' },
    { value: 'verlof', label: 'Verlof' },
    { value: 'overig', label: 'Overig' },
    { value: 'none', label: 'Geen reden ingevuld' },
];

/** Hoe een ontbrekend veld in de melding heet. */
const MISSING_LABELS = {
    HalfDayMethod: 'hoe een dagdeel telt',
    HalfDayHours: 'uren per dagdeel (een getal groter dan 0)',
    MaxHalfDaysPerDay: 'maximum per dag (een heel getal, of leeg)',
    AbsenceBilling: 'of afwezige dagen meetellen',
};

/**
 * @returns {JSX.Element}
 */
const BillingRulesSettings = () => {
    const [readError, setReadError] = useState(false);
    // `''` zolang het document onderweg is; met `onError` is een mislukte read
    // niet langer een eindeloos 'laden'.
    // AI-CONTEXT: See docs/pitfalls.md#database-firestore-silent-listener-failure
    const compagny = useFirestoreQuery('CompagnyMeta', {
        onError: (error) => {
            console.error('BillingRulesSettings: lezen mislukt', error);
            setReadError(true);
        },
    });
    const { save, status } = useAutosaveField('CompagnyMeta');
    const [saveError, setSaveError] = useState(false);

    const loaded = Array.isArray(compagny);
    const comp = loaded ? compagny[0] : undefined;
    const raw = comp?.[FIELD] || {};
    const { missing } = resolveBillingRules(raw);

    /**
     * Een keuze, geen toetsaanslag: meteen schrijven. Een mislukte opslag blijft
     * zichtbaar staan tot de volgende keuze lukt.
     * @param {object} fields
     */
    const writeNow = async (fields) => {
        setSaveError(false);
        try {
            await db.collection('CompagnyMeta').doc(comp.docid).update(fields);
        } catch (error) {
            console.error('BillingRulesSettings: opslaan mislukt', error);
            setSaveError(true);
        }
    };

    /** @param {string} key @param {string} value */
    const selectHandler = (key, value) => writeNow({ [`${FIELD}.${key}`]: value });

    /** @param {string} key @param {string} value */
    const numberHandler = (key, value) => save(comp.docid, { [`${FIELD}.${key}`]: parseTariff(value) });

    /** @param {string} reason @param {boolean} checked */
    const reasonHandler = (reason, checked) => {
        const current = Array.isArray(raw.BillableAbsenceReasons) ? raw.BillableAbsenceReasons : [];
        const next = checked ? [...current.filter((r) => r !== reason), reason] : current.filter((r) => r !== reason);
        writeNow({ [`${FIELD}.BillableAbsenceReasons`]: next });
    };

    return (
        <div className="dashboard-container billing-rules">
            <h2>Rekenregels</h2>
            {readError ? (
                <p className="billing-rules__error" role="alert">De instellingen zijn niet te lezen. Herlaad de pagina of vraag een beheerder.</p>
            ) : !loaded ? (
                <p className="status-config-loading">Instellingen worden geladen…</p>
            ) : !comp ? (
                <p className="billing-rules__error" role="alert">Geen instellingen gevonden voor deze organisatie.</p>
            ) : (
                <ReadOnlyGuard edit="billing.edit">
                    <div className="billing-rules__fields">
                        <label className="billing-rules__field">
                            <span>Hoe telt een dagdeel?</span>
                            <select
                                value={raw.HalfDayMethod || ''}
                                onChange={(e) => selectHandler('HalfDayMethod', e.target.value)}
                            >
                                <option value="">-- Kies hoe een dagdeel telt --</option>
                                {METHOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </label>

                        {raw.HalfDayMethod !== 'onePerDay' && (
                            <label className="billing-rules__field">
                                <span>Uren per dagdeel</span>
                                {/* Ongecontroleerd: het document is er al als dit veld aanhaakt.
                                    AI-CONTEXT: See docs/pitfalls.md#ui-tabletitlecell-onchange-refocus */}
                                <input
                                    type="number"
                                    min="0"
                                    step="0.25"
                                    defaultValue={raw.HalfDayHours ?? ''}
                                    onChange={(e) => numberHandler('HalfDayHours', e.target.value)}
                                />
                            </label>
                        )}

                        <label className="billing-rules__field">
                            <span>Maximum dagdelen per dag</span>
                            <input
                                type="number"
                                min="1"
                                step="1"
                                placeholder="Geen maximum"
                                defaultValue={raw.MaxHalfDaysPerDay ?? ''}
                                onChange={(e) => numberHandler('MaxHalfDaysPerDay', e.target.value)}
                            />
                        </label>

                        <label className="billing-rules__field">
                            <span>Tellen afwezige dagen mee?</span>
                            <select
                                value={raw.AbsenceBilling || ''}
                                onChange={(e) => selectHandler('AbsenceBilling', e.target.value)}
                            >
                                <option value="">-- Kies --</option>
                                {ABSENCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </label>

                        {raw.AbsenceBilling === 'perReason' && (
                            <div className="billing-rules__reasons" role="group" aria-label="Redenen die meetellen">
                                {REASON_OPTIONS.map((o) => (
                                    <label key={o.value} className="billing-rules__reason">
                                        <input
                                            type="checkbox"
                                            checked={(raw.BillableAbsenceReasons || []).includes(o.value)}
                                            onChange={(e) => reasonHandler(o.value, e.target.checked)}
                                        />
                                        {o.label}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </ReadOnlyGuard>
            )}

            {comp && (
                <p className="billing-rules__summary" role="status">
                    {missing.length === 0
                        ? 'Compleet: de facturatie rekent met deze regels.'
                        : `Nog niet compleet — de facturatie rekent pas als dit is ingevuld: ${missing.map((m) => MISSING_LABELS[m]).join(', ')}.`}
                </p>
            )}
            {saveError && (
                <p className="billing-rules__error" role="alert">Niet opgeslagen. Probeer het opnieuw.</p>
            )}
            <AutosaveStatus status={status} reserve />
        </div>
    );
};

export default BillingRulesSettings;
